import {Slot} from "@radix-ui/react-slot"
import type {VariantProps} from "class-variance-authority"
import {cva} from "class-variance-authority"
import type React from "react"

import {cn} from "@/lib/cn"

/**
 * DIZED pill button. Gradient surfaces carry an oversized background that
 * shifts on hover (the "bloom"), a 1px lit edge, and a press scale.
 * Sizes follow the style guide: default 40px/r20, mini 20px/r10.
 */
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 select-none whitespace-nowrap",
    "appearance-none border-0 bg-transparent",
    "font-display font-bold uppercase tracking-wide",
    "transition-[background-position,box-shadow,transform,opacity] duration-150 ease-out",
    "bg-[length:150%_100%] bg-[position:0%_0%] hover:bg-[position:100%_0%]",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    "active:scale-[0.98]",
    "disabled:pointer-events-none aria-disabled:pointer-events-none",
  ],
  {
    variants: {
      variant: {
        action: [
          "bg-action text-action-foreground edge-lit",
          "hover:shadow-card",
          "disabled:bg-action-disabled disabled:text-disabled-foreground disabled:shadow-none",
          "aria-disabled:bg-action-disabled aria-disabled:text-disabled-foreground aria-disabled:shadow-none",
        ],
        premium: [
          "bg-premium text-premium-foreground edge-lit",
          "hover:shadow-card",
          "disabled:bg-premium-disabled disabled:text-disabled-foreground disabled:shadow-none",
          "aria-disabled:bg-premium-disabled aria-disabled:text-disabled-foreground aria-disabled:shadow-none",
        ],
        secondary: [
          "bg-none border border-solid border-secondary-border text-secondary-foreground",
          "hover:border-ring hover:text-foreground",
          "disabled:border-secondary-border/50 disabled:text-disabled-foreground",
          "aria-disabled:border-secondary-border/50 aria-disabled:text-disabled-foreground",
        ],
        ghost: [
          "bg-none text-foreground hover:bg-foreground/10",
          "disabled:text-disabled-foreground aria-disabled:text-disabled-foreground",
        ],
        buy: [
          "bg-none bg-buy text-buy-foreground edge-lit",
          "hover:brightness-105 hover:shadow-card",
          "disabled:opacity-60 aria-disabled:opacity-60",
        ],
        link: [
          "bg-none text-link underline underline-offset-4 normal-case tracking-normal font-sans",
          "hover:opacity-80",
        ],
      },
      size: {
        default: "h-10 rounded-pill px-6 text-[15px] md:text-lg",
        mini: "h-5 rounded-mini px-3 text-[11px]",
        lg: "h-12 rounded-[24px] px-8 text-lg",
        icon: "size-10 rounded-full p-0",
        iconSm: "size-8 rounded-full p-0",
      },
    },
    defaultVariants: {
      variant: "action",
      size: "default",
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render into the child element (e.g. next/link) instead of a <button>. */
  asChild?: boolean
  ref?: React.Ref<HTMLButtonElement>
}

/** DIZED design-system button. */
export const Button = ({
  className,
  variant,
  size,
  asChild = false,
  type,
  ...props
}: ButtonProps) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({variant, size}), className)}
      type={asChild ? undefined : (type ?? "button")}
      {...props}
    />
  )
}

export {buttonVariants}
